import type { ResultOf } from "@graphql-typed-document-node/core";
import { safe, ErrorADT } from "pb.safe";

import {
	$,
	query,
	TransactionType,
} from "../../../../schema.dev-sdk-api-appsync.generated";
import { getDeviceSettings } from "../shared/get-device-settings";
import type { ApiClient } from "../shared/shared/api-client";
import { transactionFields } from "./transaction-fields";

export type Transactions = Exclude<
	Awaited<ReturnType<typeof getTransactions>>,
	Error
>["transactions"];

export async function getTransactions({
	apiClient,
	deviceId,
	reference,
}: {
	apiClient: ApiClient;
	deviceId: string;
	reference?: string;
}): Promise<
	| ResultOf<typeof getTransactions.gql.getTransactions>["getTransactions"]
	| ErrorADT<"NetworkFailure">
> {
	const defaultOptions = {
		deviceUuid: deviceId,
		limit: 50,
		filter: {
			type: {
				in: [TransactionType.PURCHASE, TransactionType.REFUND],
			},
			timestamp: {
				gt: new Date(
					Date.now() - 31 * 24 * 60 * 60 * 1000 /* 31 days */,
				).toISOString(),
			},
		},
	};

	if (reference) {
		const response = await safe(() =>
			apiClient.request(getTransactions.gql.getTransactions, {
				...defaultOptions,
				filter: {
					...defaultOptions.filter,
					externalReference: { eq: reference },
				},
			}),
		);

		if (response instanceof Error) {
			return ErrorADT("NetworkFailure");
		}

		return response.getTransactions;
	}

	// if no reference get the last known transactions for paired terminal
	// with the source SDK
	const deviceSettings = await getDeviceSettings({ apiClient, deviceId });

	if (deviceSettings instanceof Error) {
		return ErrorADT("NetworkFailure");
	}

	const terminalUuid = deviceSettings?.terminal?.id;

	if (!terminalUuid) {
		return ErrorADT("NetworkFailure");
	}

	const response = await safe(() =>
		apiClient.request(getTransactions.gql.getTransactions, {
			...defaultOptions,
			filter: {
				deviceUuid: { eq: terminalUuid },
				source: { eq: "SDK" },
			},
			limit: 1,
		}),
	);

	if (response instanceof Error) {
		return ErrorADT("NetworkFailure");
	}

	return response.getTransactions;
}

getTransactions.gql = {
	getTransactions: query("getTransactions", (q) => [
		q.getTransactions(
			{
				deviceUuid: $("deviceUuid"),
				limit: $("limit"),
				filter: $("filter"),
				nextToken: $("nextToken"),
			},
			(c) => [
				c.nextToken((n) => [n.entityUuid, n.id, n.type]),
				c.transactions((t) => [...transactionFields(t)]),
			],
		),
	]),
};
